import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Eye, FileText, MessageSquare, ThumbsUp, Heart, Lightbulb, HelpCircle,
  TrendingUp, ArrowRight, Bookmark, Mail, BarChart3, Clock, User
} from "lucide-react";
import api from "@/lib/api";

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [analyticsRes, catRes] = await Promise.all([
          api.get("/admin/analytics"),
          api.get("/categories"),
        ]);
        setData(analyticsRes.data);
        const map = {};
        (catRes.data || []).forEach(c => { map[c.id] = c; });
        setCategories(map);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div><Skeleton className="h-10 w-64 mb-8" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}</div><Skeleton className="h-96" /></div>
    );
  }

  if (!data) return <p className="text-muted-foreground font-sans">Failed to load analytics</p>;

  const REACTION_ICONS = { like: ThumbsUp, love: Heart, insightful: Lightbulb, helpful: HelpCircle };

  // Find max daily views for bar chart scaling
  const maxDailyViews = Math.max(1, ...data.daily_views.map(d => d.views));

  return (
    <div data-testid="analytics-page">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-foreground">Analytics</h1>
        <p className="text-sm font-sans text-muted-foreground mt-1">Content performance and engagement metrics</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {[
          { label: "Total Views", value: data.total_views, icon: Eye, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Articles", value: data.total_articles, icon: FileText, color: "text-purple-600", bg: "bg-purple-50" },
          { label: "Comments", value: data.total_comments, icon: MessageSquare, color: "text-green-600", bg: "bg-green-50" },
          { label: "Reactions", value: data.total_reactions, icon: ThumbsUp, color: "text-orange-600", bg: "bg-orange-50" },
          { label: "Subscribers", value: data.total_subscribers, icon: Mail, color: "text-pink-600", bg: "bg-pink-50" },
          { label: "Bookmarks", value: data.total_bookmarks, icon: Bookmark, color: "text-amber-600", bg: "bg-amber-50" },
        ].map(stat => (
          <Card key={stat.label} className="border-slate-200 dark:border-slate-700" data-testid={`analytics-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-4">
              <div className={`w-8 h-8 ${stat.bg} dark:bg-slate-700 flex items-center justify-center mb-2`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <p className="text-2xl font-serif font-bold text-foreground">{stat.value.toLocaleString()}</p>
              <p className="text-[11px] font-sans text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Daily Views Chart */}
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-700" data-testid="daily-views-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#1E88E5]" /> Views (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_views.length === 0 ? (
              <div className="text-center py-12">
                <Eye className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-sans text-muted-foreground">No view data yet. Views are tracked when users visit articles.</p>
              </div>
            ) : (
              <div className="flex items-end gap-1 h-48 mt-4">
                {data.daily_views.map((d, i) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.views} views`}>
                    <span className="text-[10px] font-sans text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">{d.views}</span>
                    <div
                      className="w-full bg-[#1E88E5] hover:bg-[#1565C0] transition-colors rounded-t-sm min-h-[2px]"
                      style={{ height: `${Math.max(2, (d.views / maxDailyViews) * 100)}%` }}
                    />
                    {i % 5 === 0 && (
                      <span className="text-[9px] font-sans text-muted-foreground mt-1">{d.date.slice(5)}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reaction Breakdown */}
        <Card className="border-slate-200 dark:border-slate-700" data-testid="reaction-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg">Reactions</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(data.reaction_stats).length === 0 ? (
              <p className="text-sm font-sans text-muted-foreground text-center py-8">No reactions yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(data.reaction_stats).map(([type, count]) => {
                  const Icon = REACTION_ICONS[type] || ThumbsUp;
                  const totalRxn = Object.values(data.reaction_stats).reduce((a, b) => a + b, 0);
                  const pct = totalRxn > 0 ? Math.round((count / totalRxn) * 100) : 0;
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-sans font-medium text-foreground capitalize">{type}</span>
                          <span className="text-xs font-sans text-muted-foreground">{count} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-[#1E88E5] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Articles */}
        <Card className="border-slate-200 dark:border-slate-700" data-testid="top-articles">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#26A69A]" /> Trending Articles
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_articles.length === 0 ? (
              <p className="text-sm font-sans text-muted-foreground text-center py-8">No article views tracked yet</p>
            ) : (
              <div className="space-y-0 divide-y divide-slate-100 dark:divide-slate-700">
                {data.top_articles.map((art, i) => (
                  <div key={art.id} className="flex items-center gap-3 py-3" data-testid={`top-article-${i}`}>
                    <span className="text-lg font-serif font-bold text-slate-200 dark:text-slate-700 w-6 text-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <Link to={`/admin/articles/${art.id}/edit`} className="text-sm font-sans font-medium text-foreground hover:text-[#1E88E5] transition-colors line-clamp-1">
                        {art.title}
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] font-sans text-muted-foreground">
                        {art.author_name && <span>{art.author_name}</span>}
                        {categories[art.category_id] && (
                          <Badge variant="outline" className="text-[9px] py-0" style={{ borderColor: categories[art.category_id].color, color: categories[art.category_id].color }}>
                            {categories[art.category_id].name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-sans font-bold text-foreground">{(art.view_count || 0).toLocaleString()}</span>
                      <p className="text-[10px] font-sans text-muted-foreground">views</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Performance + Recent Comments */}
        <div className="space-y-6">
          {/* Views by Category */}
          <Card className="border-slate-200 dark:border-slate-700" data-testid="category-views">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg">Views by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {data.category_views.length === 0 ? (
                <p className="text-sm font-sans text-muted-foreground text-center py-6">No category data yet</p>
              ) : (
                <div className="space-y-2">
                  {data.category_views.map(cv => {
                    const cat = categories[cv.category_id];
                    const maxCatViews = Math.max(1, ...data.category_views.map(c => c.views));
                    return (
                      <div key={cv.category_id} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat?.color || "#94a3b8" }} />
                        <span className="text-sm font-sans text-foreground flex-1 truncate">{cat?.name || cv.category_id}</span>
                        <div className="w-24 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(cv.views / maxCatViews) * 100}%`, backgroundColor: cat?.color || "#1E88E5" }} />
                        </div>
                        <span className="text-xs font-sans text-muted-foreground w-10 text-right">{cv.views}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Comments */}
          <Card className="border-slate-200 dark:border-slate-700" data-testid="recent-comments">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-green-500" /> Recent Comments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.recent_comments.length === 0 ? (
                <p className="text-sm font-sans text-muted-foreground text-center py-6">No comments yet</p>
              ) : (
                <div className="space-y-3">
                  {data.recent_comments.map(c => (
                    <div key={c.id} className="border-b border-slate-100 dark:border-slate-700 pb-3 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="font-sans font-medium text-sm text-foreground">{c.user_name}</span>
                        <span className="text-[11px] font-sans text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm font-sans text-muted-foreground mt-1 line-clamp-2">{c.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
